// src/components/QuizListSection.tsx
import React from 'react';
import { X } from 'lucide-react';
import { useUserProfile } from '../../hooks/useUserProfile';
import { Quiz, Profile, QuizSummary } from '../../types';
import { useMyQuizSelection } from '../../hooks/useMyQuizSelection'
import { hideQuiz } from '../../services/myQuizSelectionService';
import { logger } from '../../utils/logger'
/** QuizListSection コンポーネントのProps型定義 */
interface QuizListSectionProps {
  startQuiz: (quizId: string) => void;
  handlePageChange: (page: string) => void;
  userId: string | undefined;
}

const QuizListSection: React.FC<QuizListSectionProps> = ({
  startQuiz,
  handlePageChange,
  userId,
}) => {
  // useMyQuizSelection フックを使用してクイズデータをフェッチ
  const { myQuizSelection, isLoading: myQuizLoading, isError: myQuizError, mutate: myQuizMute } = useMyQuizSelection(userId || '');

  if (myQuizLoading) {
    return <div className="text-center mt-10">読み込み中...</div>;
  }
  if (myQuizError) {
    return <div className="text-center mt-10 text-red-500">プロフィールの取得に失敗しました。</div>;
  }



  // 表示するクイズをフィルタリング
  // 1. クイズの作成者IDがログインユーザーのIDと一致する
  // 2. または、ユーザーのaddedQuizzesリストにクイズIDが含まれている
  // 3. かつ、非表示クイズではない
  const visibleQuizzes = myQuizSelection?.quizSummaries.filter(
    (q: QuizSummary) => {
      // idが存在しないクイズは除外 (Firestoreから取得する際は必ずidがあるはずだが念のため)
      if (!q.id) return false;

      const isNotHidden = !myQuizSelection.hiddenQuizIdList?.includes(q.id);

      return isNotHidden;
    }
  ) || [];

  /**
* クイズを再表示するハンドラ
 */
  const handleHiddenQuiz = async (quizId: string) => {
    if (!userId) {
      logger.warn('未ログイン');
      return;
    }

    await hideQuiz(userId, quizId);
    myQuizMute();
  };

  if (visibleQuizzes?.length === 0) {
    return (
      <div className="text-center text-gray-500 text-xl py-10 bg-white rounded-2xl shadow-lg mt-12">
        <p className="mb-6 font-semibold">挑戦できるクイズがありません。</p>
        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-center sm:gap-4">
          <button
            onClick={() => handlePageChange('createQuiz')}
            className="py-3 px-8 bg-custom-gradient from-violet-500 to-purple-500 text-white rounded-full font-bold text-lg shadow-lg hover:from-violet-600 hover:to-purple-600 transform hover:scale-105 transition-all"
          >
            クイズをつくる
          </button>
          <button
            onClick={() => handlePageChange('findQuizzes')}
            className="py-3 px-8 bg-custom-gradient from-sky-400 to-cyan-400 text-white rounded-full font-bold text-lg shadow-lg hover:from-sky-500 hover:to-cyan-500 transform hover:scale-105 transition-all"
          >
            クイズをさがす
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-700 mt-12 mb-4 text-center">クイズ一覧</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleQuizzes?.map((quiz: QuizSummary) => (
          <div
            key={quiz.id}
            className="relative bg-white rounded-2xl shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 overflow-hidden transform hover:-translate-y-2 group border-2 border-transparent hover:border-cyan-400"
          >
            <button
              onClick={() => startQuiz(quiz.id)}
              className="block w-full text-left p-6 flex flex-col h-full justify-between"
            >
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{quiz.title}</h3>
                {quiz.authorNickname && (
                  <p className="text-gray-500 text-xs mt-3">作成者: {quiz.authorNickname}</p>
                )}
                <p className="text-gray-600 mt-3 text-sm">
                  問題数: {quiz.questionCount || 0}
                </p>
                <div className="flex flex-wrap gap-2 mt-2"> {/* Added a div to wrap categories */}
                  {Array.isArray(quiz.category) ? (
                    quiz.category.map((cat, index) => (
                      <span
                        key={index}
                        className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium"
                      >
                        {cat}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium">
                      {quiz.category}
                    </span>
                  )}
                </div>
              </div>
            </button>
            <div className="absolute top-4 right-4 sm:opacity-0 sm:group-hover:opacity-100 opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  hideQuiz(userId, quiz.id);
                  myQuizMute();
                }}
                className="p-2 bg-gray-200 rounded-full text-gray-600 hover:bg-red-200 hover:text-red-600 transition duration-200"
                aria-label="クイズを非表示にする"
              >
                <X size={18} className="sm:opacity-100 opacity-50" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuizListSection;
